import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, PackageCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, money, getToken } from "@/lib/api";
import MarkSentDialog from "@/components/MarkSentDialog";
import ShipmentList from "@/components/ShipmentList";
import { EmptyState, StatusBadge } from "@/components/Bits";
import { InclusionChips } from "@/pages/InclusionsPage";

const DropGroup = ({ g }) => (
  <div data-testid={`inclusion-drop-${g.drop_id}`} className="rounded-2xl border border-white/5 bg-zinc-900/60 p-5">
    <div className="flex flex-wrap items-center gap-2">
      <Link to={`/drops/${g.drop_id}`} className="font-display text-lg font-bold hover:text-pink-200 transition-colors">{g.drop_title}</Link>
      <StatusBadge status={g.status} />
      {Object.keys(g.sent).length > 0 && (
        <span data-testid={`inclusion-drop-sent-${g.drop_id}`} className="rounded-full bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 text-xs text-emerald-300">
          {Object.keys(g.owed).length ? "Partly sent" : "Inclusions sent"}
        </span>
      )}
    </div>
    <div className="mt-3"><InclusionChips totals={g.totals} testPrefix={`inclusion-drop-total-${g.drop_id}`} /></div>
    <div className="mt-4 grid gap-1.5">
      {g.claims.map((c, i) => (
        <div key={`${c.member}-${i}`} className="flex items-center gap-3 rounded-lg bg-zinc-950/60 px-3 py-2 text-sm">
          <span className="font-medium flex-1">{c.member}</span>
          <span className="font-mono text-xs text-zinc-500">Set #{c.set_number}</span>
          <span className="font-mono text-xs w-16 text-right">{money(c.price)}</span>
          <StatusBadge status={c.counted ? "approved" : "pending"} label={c.counted ? "Paid" : "Unpaid"} />
        </div>
      ))}
    </div>
  </div>
);

const TotalsCard = ({ title, totals, testId, accent }) => (
  <div data-testid={testId} className={`rounded-2xl border p-5 ${accent ? "border-pink-500/30 bg-pink-500/[0.05]" : "border-white/5 bg-zinc-900/60"}`}>
    <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-2">{title}</div>
    <InclusionChips totals={totals} testPrefix={testId} empty={accent ? "All caught up" : "None yet"} />
  </div>
);

export default function InclusionDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [dialog, setDialog] = useState(false);
  const isAdmin = !!getToken();
  const load = useCallback(() => {
    api.get(`/inclusion-counts/${id}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (error) return <EmptyState testId="inclusion-detail-not-found" title="Joiner not found" />;
  if (!data) return <div className="text-zinc-500 text-sm">Loading…</div>;

  return (
    <div className="rise">
      <Link to="/inclusions" data-testid="back-to-inclusions" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors">
        <ArrowLeft className="h-4 w-4" /> All inclusions
      </Link>
      <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
        <h1 data-testid="inclusion-detail-name" className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">{data.joiner.name}</h1>
        {isAdmin && (
          <Button data-testid="mark-sent-open-btn" onClick={() => setDialog(true)} className="rounded-full bg-pink-500 hover:bg-pink-400 text-white">
            <PackageCheck className="h-4 w-4 mr-2" /> Mark inclusions sent
          </Button>
        )}
      </div>
      <div className="mt-5 grid gap-3 md:grid-cols-3">
        <TotalsCard title="Still owed" totals={data.owed} testId="inclusion-detail-owed" accent />
        <TotalsCard title="Sent" totals={data.sent} testId="inclusion-detail-sent" />
        <TotalsCard title="Total earned" totals={data.totals} testId="inclusion-detail-total" />
      </div>
      <ShipmentList shipments={data.shipments} isAdmin={isAdmin} onChange={load} />
      {isAdmin && <MarkSentDialog open={dialog} onOpenChange={setDialog} joinerId={id} drops={data.drops} onDone={() => { setDialog(false); load(); }} />}
      <div className="mt-8 space-y-4">
        {data.drops.length === 0 && <EmptyState title="No active claims yet" />}
        {data.drops.map((g) => <DropGroup key={g.drop_id} g={g} />)}
      </div>
    </div>
  );
}
